import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import SortableTh, { sortRows } from "@/components/SortableTh";
import { TICKET_PRIORITIES } from "@/lib/tickets";
import { Plus, Pencil, Trash2 } from "lucide-react";

const AUDIENCE_OPTS = [
    { v: "all", label: "Todos" },
    { v: "customer", label: "Solo clientes" },
    { v: "staff", label: "Solo staff (agentes/admin)" },
];

const EMPTY = { title: "", category: "", subject: "", description: "", priority: "medium", tags: "", group_id: "", audience: "all", is_active: true, is_internal_use: false };

export default function TicketTemplates() {
    const [rows, setRows] = useState([]);
    const [groups, setGroups] = useState([]);
    const [error, setError] = useState("");
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState({ field: "title", dir: "asc" });

    const filtered = useMemo(() => {
        const base = rows.filter((t) => !q || `${t.title} ${t.category || ""} ${t.subject} ${(t.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
        return sortRows(base, sort);
    }, [rows, q, sort]);

    const load = async () => {
        try {
            const [t, g] = await Promise.all([api.get("/ticket-templates"), api.get("/groups")]);
            setRows(t.data); setGroups(g.data);
        } catch (err) { setError(formatApiError(err)); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setForm(EMPTY); setModal({ mode: "create" }); };
    const openEdit = (t) => {
        setForm({
            title: t.title, category: t.category || "", subject: t.subject, description: t.description,
            priority: t.priority || "medium",
            tags: (t.tags || []).join(", "),
            group_id: t.group_id || "",
            audience: t.audience || "all",
            is_active: t.is_active !== false,
            is_internal_use: !!t.is_internal_use,
        });
        setModal({ mode: "edit", template: t });
    };

    const save = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const payload = {
                title: form.title.trim(),
                subject: form.subject.trim(),
                description: form.description,
                priority: form.priority,
                tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
                group_id: form.group_id || null,
                category: form.category.trim() || null,
                audience: form.audience,
                is_active: !!form.is_active,
                is_internal_use: !!form.is_internal_use,
            };
            if (modal.mode === "create") await api.post("/ticket-templates", payload);
            else await api.patch(`/ticket-templates/${modal.template.template_id}`, payload);
            setModal(null); await load();
        } catch (err) { setError(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const remove = async (t) => {
        if (!window.confirm(`¿Eliminar plantilla "${t.title}"?`)) return;
        try { await api.delete(`/ticket-templates/${t.template_id}`); await load(); }
        catch (err) { setError(formatApiError(err)); }
    };

    return (
        <div data-testid="ticket-templates-page">
            <div className="page-head">
                <div>
                    <h1>Tipos de solicitud</h1>
                    <p>Plantillas que aparecen en el formulario de nuevo ticket para que los clientes elijan el motivo y rellenen lo mínimo.</p>
                </div>
                <button className="btn btn-primary" onClick={openCreate} data-testid="ttpl-create-btn"><Plus size={16} /> Nueva plantilla</button>
            </div>

            {error && <div className="alert alert-error" data-testid="ttpl-error">{error}</div>}

            <div className="card">
                <div className="card-header">
                    <h3>Listado ({filtered.length} de {rows.length})</h3>
                    <div className="tbar"><input className="input" style={{ width: 280 }} placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ttpl-search" /></div>
                </div>
                <div className="table-wrap">
                    <table className="t">
                        <thead>
                            <tr>
                                <SortableTh field="title" sort={sort} onChange={setSort}>Título</SortableTh>
                                <SortableTh field="category" sort={sort} onChange={setSort}>Categoría</SortableTh>
                                <SortableTh field="priority" sort={sort} onChange={setSort}>Prioridad</SortableTh>
                                <SortableTh field="audience" sort={sort} onChange={setSort}>Audiencia</SortableTh>
                                <SortableTh field="is_active" sort={sort} onChange={setSort}>Estado</SortableTh>
                                <th style={{ textAlign: "right", padding: "10px 12px", color: "#A3A3A8", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: "1px solid #26262B" }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && <tr><td colSpan={6} className="text-muted" style={{ textAlign: "center", padding: 30 }}>Sin plantillas todavía. Crea la primera con "Nueva plantilla".</td></tr>}
                            {filtered.map((t) => (
                                <tr key={t.template_id} data-testid={`ttpl-row-${t.template_id}`}>
                                    <td><b>{t.title}</b>{t.is_internal_use && <span className="badge" style={{ marginLeft: 8, background: "#F97316", color: "#fff", borderColor: "#EA580C", fontSize: 10 }} data-testid={`ttpl-internal-use-${t.template_id}`}>Uso Interno</span>}<div className="text-muted text-xs" style={{ marginTop: 2, maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</div></td>
                                    <td className="text-sm text-muted">{t.category || "—"}</td>
                                    <td className="text-sm">{t.priority}</td>
                                    <td className="text-sm">{AUDIENCE_OPTS.find((o) => o.v === t.audience)?.label || t.audience}</td>
                                    <td>
                                        {t.is_active
                                            ? <span className="badge badge-success">Activa</span>
                                            : <span className="badge badge-muted">Inactiva</span>}
                                    </td>
                                    <td>
                                        <div className="actions">
                                            <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)} data-testid={`ttpl-edit-${t.template_id}`}><Pencil size={13} /> Editar</button>
                                            <button className="btn btn-danger btn-sm" onClick={() => remove(t)} data-testid={`ttpl-delete-${t.template_id}`}><Trash2 size={13} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                open={!!modal}
                onClose={() => setModal(null)}
                title={modal?.mode === "create" ? "Nueva plantilla" : "Editar plantilla"}
                testId="ttpl-modal"
                footer={<>
                    <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
                    <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="ttpl-modal-save">{saving ? <span className="loader" /> : "Guardar"}</button>
                </>}
            >
                <form onSubmit={save} className="row" style={{ gap: 14 }}>
                    <div className="row row-2">
                        <div className="field">
                            <label>Título visible *</label>
                            <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={120} placeholder="ej. No puedo iniciar sesión" data-testid="ttpl-modal-title" />
                        </div>
                        <div className="field">
                            <label>Categoría</label>
                            <input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} maxLength={60} placeholder="ej. Acceso, Facturación…" data-testid="ttpl-modal-category" />
                        </div>
                    </div>
                    <div className="field">
                        <label>Asunto prefilled *</label>
                        <input className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required maxLength={200} data-testid="ttpl-modal-subject" />
                    </div>
                    <div className="field">
                        <label>Descripción prefilled *</label>
                        <textarea className="textarea" rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="Pídele al cliente los datos mínimos que necesitas para empezar a resolver." data-testid="ttpl-modal-description" />
                    </div>
                    <div className="row row-2">
                        <div className="field">
                            <label>Prioridad default</label>
                            <select className="select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="ttpl-modal-priority">
                                {TICKET_PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                            </select>
                        </div>
                        <div className="field">
                            <label>Etiquetas (separadas por coma)</label>
                            <input className="input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="ej. acceso, login" data-testid="ttpl-modal-tags" />
                        </div>
                    </div>
                    <div className="row row-2">
                        <div className="field">
                            <label>Grupo / cola default</label>
                            <select className="select" value={form.group_id} onChange={(e) => setForm({ ...form, group_id: e.target.value })} data-testid="ttpl-modal-group">
                                <option value="">— ninguno —</option>
                                {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
                            </select>
                        </div>
                        <div className="field">
                            <label>Audiencia</label>
                            <select className="select" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="ttpl-modal-audience">
                                {AUDIENCE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="field" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                        <input id="ttpl-active" type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="ttpl-modal-active" />
                        <label htmlFor="ttpl-active" style={{ margin: 0 }}>Plantilla activa (visible para los usuarios al crear ticket)</label>
                    </div>
                    <div className="field" style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
                        <input id="ttpl-internal" type="checkbox" checked={!!form.is_internal_use} onChange={(e) => setForm({ ...form, is_internal_use: e.target.checked })} data-testid="ttpl-modal-internal-use" style={{ marginTop: 3 }} />
                        <label htmlFor="ttpl-internal" style={{ margin: 0 }}>
                            <b>Uso Interno</b>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                                Solo personas con el flag "Uso Interno" verán esta plantilla y podrán crear tickets de este tipo.
                            </div>
                        </label>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
